import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import React, { FC, useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';

import AdornmentTooltip from './AdornmentTooltip/AdornmentTooltip';
import FlashMessageCreate from './FlashMessage';

import Input from './Input/Input';

import API from '../API';
import { FlashMessageType } from '../const';
import { FormValuesFromFields, genderTable, InputFields, normalizeAndValidateDate, normalizeToHalfWidth, RawField, withValidation } from '../forms';

type TRegistrationModalManual = {
  isDisabled: boolean;
  setIsDisabled: (value: boolean) => void;
  setBeforeClose: (value: { flag: boolean; message: string }) => void;
  removeModal: (value: boolean) => void;
};

const RegistrationModalManual: FC<TRegistrationModalManual> = ({ isDisabled, setIsDisabled, setBeforeClose, removeModal }) => {
  const FormFields = {
    prepaidCode: { ...InputFields.prepaidCode },
    hospitalPatientId: { ...InputFields.hospitalPatientId, required: true },
    patientName: { ...InputFields.patientName, required: !window.__GlobalVars.hospitalAnonymizeDataToggle },
    gender: { ...InputFields.gender, required: true },
    birthdate: { ...InputFields.birthdate, required: true },
    email: { ...InputFields.email },
  } as const satisfies Record<string, RawField>;

  type RegistrationFormValues = FormValuesFromFields<typeof FormFields>;

  const methods = useForm<RegistrationFormValues>({
    defaultValues: {
      prepaidCode: '',
      hospitalPatientId: '',
      patientName: '',
      gender: 'other',
      birthdate: '',
      email: '',
    },
  });
  const {
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
    clearErrors,
    control,
  } = methods;
  const onSubmit = withValidation(FormFields, methods, () => {
    setIsDisabled(true);

    const formData = new FormData();
    let message = '';
    let title = '';
    let type = FlashMessageType.Success;

    for (const [key, value] of Object.entries(getValues())) {
      formData.set(key, value);
    }

    API.setPatientManual(formData)
      .then(({ data }) => {
        message = data.msgUser;
        title = data.msgUserTitle || '';
        type = data.msgType;
        window.PatientTable.update();
        removeModal(true); // Optional
      })
      .catch((err) => {
        message = err.response.data.msgUser;
        title = err.response.data.msgUserTitle || '';
        type = err.response.data.msgType;
      })
      .finally(() => {
        FlashMessageCreate({ title, message, type, isSelfDestroyable: true });
        setIsDisabled(false);
      });
  });
  useEffect(() => {
    if (isDirty) {
      setBeforeClose({ flag: true, message: '受診者登録が完了していませんが、本当にこの画面を閉じますか？' });
    }

    return () => {
      setBeforeClose({ flag: false, message: '' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  return (
    <form className='registration-modal__manual' onSubmit={handleSubmit(onSubmit)}>
      <div className='registration-modal__border registration-modal__border--secondary'>
        <div className='registration-modal__header registration-modal__header--secondary'>
          受診者がBrainSuiteチケットをお持ちの場合は、チケット番号を入力してください。
        </div>
        <div style={{ padding: '1rem' }}>
          <Controller
            name={FormFields.prepaidCode.name}
            control={control}
            rules={{ required: FormFields.prepaidCode.required }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={FormFields.prepaidCode.label}
                errorText={fieldState.error?.message}
                disabled={isDisabled}
                inputProps={{
                  tabIndex: FormFields.prepaidCode.tabIndex,
                  required: FormFields.prepaidCode.required,
                  maxLength: FormFields.prepaidCode.maxLength,
                  pattern: FormFields.prepaidCode.pattern,
                }}
                type={FormFields.prepaidCode.type}
                size='small'
                fullWidth
                customAdornments={[
                  <AdornmentTooltip
                    key='help'
                    type='help'
                    title={
                      <>
                        事前にBrainSuiteをご購入およびお支払いしている場合、BrainSuiteチケットをお渡ししております。
                        チケット番号を入力することで、ご利用者様の窓口負担金を調整いたします。
                      </>
                    }
                  />,
                ]}
              />
            )}
          />
        </div>
      </div>
      <div className='registration-modal__border registration-modal__border--primary'>
        <div className='registration-modal__header registration-modal__header--primary'>必須項目</div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            justifyContent: 'space-around',
            justifyItems: 'center',
            padding: '1rem',
          }}
        >
          <Controller
            name={FormFields.hospitalPatientId.name}
            control={control}
            rules={{ required: FormFields.hospitalPatientId.required }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={FormFields.hospitalPatientId.label}
                errorText={fieldState.error?.message}
                disabled={isDisabled}
                inputProps={{
                  tabIndex: FormFields.hospitalPatientId.tabIndex,
                  required: FormFields.hospitalPatientId.required,
                  maxLength: FormFields.hospitalPatientId.maxLength,
                  pattern: FormFields.hospitalPatientId.pattern,
                }}
                type={FormFields.hospitalPatientId.type}
                size='small'
                fullWidth
                onBlur={() => {
                  field.onBlur();
                  normalizeToHalfWidth<RegistrationFormValues>(FormFields.hospitalPatientId.name, methods);
                }}
                onChange={(evt) => {
                  field.onChange(evt);
                  clearErrors(FormFields.hospitalPatientId.name);
                }}
              />
            )}
          />
          <Controller
            name={FormFields.patientName.name}
            control={control}
            rules={{ required: FormFields.patientName.required }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={FormFields.patientName.label}
                errorText={fieldState.error?.message}
                disabled={isDisabled}
                inputProps={{
                  tabIndex: FormFields.patientName.tabIndex,
                  required: FormFields.patientName.required,
                  maxLength: FormFields.patientName.maxLength,
                  pattern: FormFields.patientName.pattern,
                }}
                type={FormFields.patientName.type}
                size='small'
                fullWidth
              />
            )}
          />
          <FormControl variant='outlined' size='small' error={!!errors.gender} disabled={isDisabled}>
            <Controller
              name={FormFields.gender.name}
              control={control}
              rules={{ required: FormFields.gender.required }}
              render={({ field }) => (
                <RadioGroup sx={{ justifyContent: 'start' }} row {...field}>
                  <FormControlLabel
                    value='male'
                    control={<Radio required={FormFields.gender.required} inputProps={{ tabIndex: FormFields.gender.tabIndex }} />}
                    label={genderTable.male}
                  />
                  <FormControlLabel
                    value='female'
                    control={<Radio required={FormFields.gender.required} inputProps={{ tabIndex: FormFields.gender.tabIndex }} />}
                    label={genderTable.female}
                  />
                  <FormControlLabel
                    value='other'
                    control={<Radio required={FormFields.gender.required} inputProps={{ tabIndex: FormFields.gender.tabIndex }} />}
                    label={genderTable.other}
                  />
                </RadioGroup>
              )}
            />
          </FormControl>
          <Controller
            name={FormFields.birthdate.name}
            control={control}
            rules={{ required: FormFields.birthdate.required }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={FormFields.birthdate.label}
                errorText={fieldState.error?.message}
                disabled={isDisabled}
                inputProps={{
                  tabIndex: FormFields.birthdate.tabIndex,
                  required: FormFields.birthdate.required,
                  maxLength: FormFields.birthdate.maxLength,
                  pattern: FormFields.birthdate.pattern,
                }}
                type={FormFields.birthdate.type}
                size='small'
                fullWidth
                onBlur={() => {
                  field.onBlur();
                  normalizeAndValidateDate<RegistrationFormValues>(FormFields.birthdate.name, methods);
                }}
                onChange={(evt) => {
                  field.onChange(evt);
                  clearErrors(FormFields.birthdate.name);
                }}
              />
            )}
          />
          <Controller
            name={FormFields.email.name}
            control={control}
            rules={{ required: FormFields.email.required }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={FormFields.email.label}
                errorText={fieldState.error?.message}
                disabled={isDisabled}
                inputProps={{
                  tabIndex: FormFields.email.tabIndex,
                  required: FormFields.email.required,
                  maxLength: FormFields.email.maxLength,
                  pattern: FormFields.email.pattern,
                }}
                type={FormFields.email.type}
                size='small'
                fullWidth
              />
            )}
          />
        </div>
      </div>
      <p style={{ fontSize: '0.875rem', fontWeight: 700 }}>※「登録」をクリックすると、受診者にオンラインテストの案内メールを送信します。</p>
      <button className={`btn btn--main ${isDisabled ? 'btn--loading' : ''}`} type='submit' disabled={isDisabled}>
        登録
      </button>
    </form>
  );
};

export default RegistrationModalManual;
